import React, { useCallback, useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import Form from "@bappweb/form"
import Panel from "@bappweb/panel"
import TextField from "@bappweb/textfield"
import Button from "@bappweb/button"
import Checkbox from "@bappweb/checkbox"
import Link from "@bappweb/link"
import Captcha from "@smpu-web/nested/form/captcha"
import FormMessage from "@smpu-web/globalmodules/form-message/index"
import Page from "@smpu-web/globalmodules/page"
import Element from "@bappweb/element"
import { numeric, RegexpTypes, required, dateFormat } from "@bappweb/utils"
import { useGetTokenByTckn1Mutation, useGetCaptchaMutation, useRefreshCaptchaMutation, useApplyMutation } from "../../../api/services"
import { getDayMonthYearFromStr } from "@smpu-web/utils/utils"
import "../assets/style.scss"
import "src/assets/styles/style.scss"
import "../../../pages/assets/style.scss"

type PropsType = {
  onError?: (event?: React.BaseSyntheticEvent, errorField?: Record<string, any>) => void
  onSubmit?: (event?: React.BaseSyntheticEvent, values?: Record<string, any>) => void
}

const getCid = () => {
  let search = window.location.search;
  let params = new URLSearchParams(search);

  return params.get('cid') ?? "AkbOrgnc"
}

function MainForm({ onError, onSubmit }: PropsType) {
  const history = useHistory()
  
  const [identityNumber, setIdentityNumber] = useState('')
  const [captchaValues, setCaptchaValues] = useState({
    tokenSessionId: "",
    captchaData: "",
    captcha: "",
    showCaptcha: false,
    captchaValidate: true
  })

  const [error, setError] = useState<string>()
  const [isFetching, setIsFetching] = useState(false)
    
  const [getCaptcha] = useGetCaptchaMutation();

  useEffect(() => {
    if (captchaValues.captchaData === "" && captchaValues.tokenSessionId !== "") {
      setIsFetching(true)
      getCaptcha({
        tckn: identityNumber,
        TokenSessionId: captchaValues.tokenSessionId
      })
        .unwrap()
        .then((payload) => {
          if (!payload.IsError) {
            setError('')
            setCaptchaValues({
              ...captchaValues,
              showCaptcha: true,
              captchaData: payload.captchaData,
              captchaValidate: true
            })
          }
          else {
            setError(payload.ErrorMessage ? payload.ErrorMessage : "")
          }
        })
        .finally(() => { setIsFetching(false) })
    }
  }, [captchaValues, getCaptcha, identityNumber])

  const [getTokenByTckn1] = useGetTokenByTckn1Mutation();

  const handleChange = useCallback((e) => {
    const name = e.target.name
    const value = e.target.value

    switch (name) {
      case "identityNumber":
        setIdentityNumber(value)
        setCaptchaValues({
          ...captchaValues,
          tokenSessionId: "",
          captchaData: "",
          showCaptcha: false
        })
        if (value.length === 11) {
          setIsFetching(true)
          getTokenByTckn1(value)
            .unwrap()
            .then((payload) => {
              if (!payload.IsError) {
                setError('')
                setCaptchaValues({
                  ...captchaValues,
                  tokenSessionId: payload.tokenSessionId,
                })
              } else {
                setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              }
            })
            .finally(() => { setIsFetching(false) })
        }
        break;
      case "captcha":
        setCaptchaValues({
          ...captchaValues,
          captcha: value,
          captchaValidate: true
        })
        break;
      default:
        break;
    }
  }, [getTokenByTckn1, captchaValues])

  const [refreshCaptcha] = useRefreshCaptchaMutation();
  const handleCaptchaRefresh = useCallback(() => {
    setIsFetching(true)
    refreshCaptcha({
      tckn: identityNumber,
      TokenSessionId: captchaValues.tokenSessionId,
    })
      .unwrap()
      .then((payload) => {
        if (!payload.IsError) {
          setError('')
          setCaptchaValues({
            ...captchaValues,
            captchaData: payload.captchaData,
            captchaValidate: true
          })
        } else {
          setError(payload.ErrorMessage ? payload.ErrorMessage : "")
        }
      })
      .finally(() => { setIsFetching(false) })
  }, [refreshCaptcha, identityNumber, captchaValues])

  const [apply] = useApplyMutation()

  const handleSubmit = useCallback(
    (e?: React.BaseSyntheticEvent, values?: Record<string, any>) => {
      if (values && values?.birthdate && values.captcha && values.ageConfirmation && values.checkboxKvkk) {
        setIsFetching(true)
        const parsedBirthDate = getDayMonthYearFromStr(values.birthdate)

        let cid = getCid()
        apply({
          tckn: values.identityNumber,
          dogumYil: parsedBirthDate.year,
          dogumAy: parsedBirthDate.month,
          dogumGun: parsedBirthDate.day,
          cepTel: "0090" + values.mobilePhone.replace(/\s/g, ''),
          captcha: values.captcha.toUpperCase(),
          ageConfirmation: values.ageConfirmation,
          kvkk: values.checkboxKvkk,
          cid: cid,
          TokenSessionId: captchaValues.tokenSessionId,
        })
          .unwrap()
          .then((payload) => {
            if (!payload.IsError) {
              setError('')
              history.push({
                pathname: "/details/smsotp",
                state: payload
              })
            } else {
              setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              window.scrollTo({
                top: 0,
              });
              refreshCaptcha({
                tckn: identityNumber,
                TokenSessionId: captchaValues.tokenSessionId
              })
                .unwrap()
                .then((payload) => {
                  if (!payload.IsError) {
                    setCaptchaValues({
                      ...captchaValues,
                      captcha: "",
                      showCaptcha: true,
                      captchaData: payload.captchaData,
                      captchaValidate: false
                    })
                  }
                  else {
                    setError(payload.ErrorMessage ? payload.ErrorMessage : "")
                  }
                })
                .finally(() => { setIsFetching(false) })
            }
          })
          .finally(() => { setIsFetching(false) })
      }
      if (onSubmit) {
        onSubmit(e, values)
      }
    },
    [history, onSubmit, apply, captchaValues, identityNumber, refreshCaptcha]
  )

  return (
    <Page>
      <Form onSubmit={handleSubmit} onChange={handleChange} grid={false}>
        <Panel hasFooter connected={false} loading={isFetching}>
          <FormMessage className={"form-msg-error"} error={error}></FormMessage>
          <Panel.Body className="bapp-panel-body-transparent">
            <Element type="h3" className="panel-body-card-title">
              MERHABA!
            </Element>
            <Element type="h3" className="panel-body-card-description">
              Başvuru için kimlik ve iletişim bilgilerine ihtiyacımız var.
            </Element>
            <TextField
              label='TC Kimlik No'
              type="number"
              name="identityNumber"
              maxLength={11}
              value={identityNumber}
              validator={RegexpTypes.Numeric}
              validations={[required("TC Kimlik Numarası alanı zorunludur."), numeric()]}
            />
            <TextField
              label='Doğum Tarihi'
              type="text"
              placeholder="GG.AA.YYYY"
              mask="99.99.9999"
              name="birthdate"
              maxLength={10}
              validations={[
                required("Doğum Tarihi alanı zorunludur."),
                dateFormat("Lütfen Geçerli Bir Tarih Giriniz")
              ]}
            />
            <TextField
              label='Cep Telefon No'
              type="text"
              placeholder="5__ ___ __ __"
              mask="999 999 99 99"
              name="mobilePhone"
              maxLength={13}
              validations={[required("Cep Telefonu alanı zorunludur.")]}
            />
            {captchaValues.showCaptcha && (
              <Captcha
                label="Güvenlik Kodu"
                id="captcha"
                type="text"
                name="captcha"
                placeholder="Güvenlik Kodu"
                captcha={captchaValues.captchaData}
                value={captchaValues.captcha}
                maxLength={4}
                maxRefreshCount={3}
                refreshClick={handleCaptchaRefresh}
                validations={[required("Lütfen güvenlik kodunu giriniz.")]}
              />
            )}
            <div className="checkbox-group">
              <Checkbox
                name="ageConfirmation"
                block
                validateOnSubmit={true}
                validations={[required("Lütfen Onay kutusunu işaretleyiniz")]}
                className="cb-position-fix"
              >
                {"27 yaşından küçüğüm ve öğrenciyim."}
              </Checkbox>
            </div>
            <div className="checkbox-group">
              <Checkbox
                name="checkboxKvkk"
                validateOnSubmit={true}
                validations={[required("Lütfen onay kutusunu işaretleyiniz.")]}
                className="cb-position-fix"
                block
              >
                {
                  "AKBANK T.A.Ş. olarak kişisel verilerini önemsiyoruz. 6698 nolu Kişisel Verilerin Korunması Kanunu kasamında kişisel verilerinin işlenmesinden doğan haklarına ve bu konudaki detaylı bilgiye "
                }
                <Link
                  href="//www.akbank.com/Documents/KISISELVERILERINKORUNMASI_TR.pdf"
                  text={"aydınlatma metninden"}
                  inline
                  target="_blank"
                />
                {" ulaşabilirsin."}
              </Checkbox>
            </div>
          </Panel.Body>
          <Panel.Footer>
            <Panel.RightFooter>
              <Button
                text="Hemen Başvur" type="submit" fetching={isFetching} disabled={isFetching}
                onClick={() =>
                  setCaptchaValues({
                    ...captchaValues,
                    captchaValidate: true
                  })
                } />
            </Panel.RightFooter>
          </Panel.Footer>
        </Panel>
      </Form>
      <Element type="p" className="form-information-text">{"**Gerekli durumlarda işleminizin tamamlanabilmesi için Müşteri İletişim Merkezimiz sizinle iletişime geçecektir."}</Element>
    </Page>
  )
}

export default MainForm
